import { useCallback, useEffect, useMemo, useState } from "react";
import type { BillingModuleId } from "./module-billing.service";
import { moduleBillingService } from "./module-billing.service";
import application from "../core/application";

type ModuleStatus = "active" | "cancel_at_period_end" | "inactive";
type StoredModuleStatus = "active" | "cancel_at_period_end" | "pending_next_cycle" | "pending_payment";

const PRODUCTS_TIERS: BillingModuleId[] = [
  "products_sales_unlimited",
  "products_sales_advanced",
  "products_sales_basic",
];

const USERS_TIERS: BillingModuleId[] = [
  "users_unlimited",
  "users_advanced",
  "users_basic",
];

const CUSTOMERS_TIERS: BillingModuleId[] = [
  "customers_credit_unlimited",
  "customers_credit_advanced",
  "customers_credit_basic",
];

export const useModuleAccess = () => {
  const [user] = application.user.useState();
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState<Record<string, { status: StoredModuleStatus; currentPeriodEnd: number; accessEndsAt?: number }>>({});

  const load = useCallback(async () => {
    if (!user) {
      setModules({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const summary = await moduleBillingService.getSummary();
      setModules(summary.modules || {});
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // Em rotas públicas, não deve quebrar render por falta de autenticação.
      if (!message.includes("UNAUTHENTICATED")) {
        console.error("Falha ao carregar módulos do plano:", err);
      }
      setModules({});
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const getStatus = useCallback(
    (moduleId: BillingModuleId): ModuleStatus => {
      const st = modules[moduleId]?.status;
      if (st === "active" || st === "cancel_at_period_end") return st;
      return "inactive";
    },
    [modules]
  );

  const isEnabled = useCallback(
    (moduleId: BillingModuleId) => getStatus(moduleId) !== "inactive",
    [getStatus]
  );

  const activeProductsTier = useMemo(() => PRODUCTS_TIERS.find((id) => isEnabled(id)) || null, [isEnabled]);
  const activeUsersTier = useMemo(() => USERS_TIERS.find((id) => isEnabled(id)) || null, [isEnabled]);
  const activeCustomersTier = useMemo(() => CUSTOMERS_TIERS.find((id) => isEnabled(id)) || null, [isEnabled]);

  const limits = useMemo(() => {
    const productsLimit =
      activeProductsTier === "products_sales_basic" ? 100 :
      activeProductsTier === "products_sales_advanced" ? 5000 :
      activeProductsTier === "products_sales_unlimited" ? Number.POSITIVE_INFINITY :
      0;
    const usersLimit =
      activeUsersTier === "users_basic" ? 3 :
      activeUsersTier === "users_advanced" ? 10 :
      activeUsersTier === "users_unlimited" ? Number.POSITIVE_INFINITY :
      0;
    const customersLimit =
      activeCustomersTier === "customers_credit_basic" ? 5 :
      activeCustomersTier === "customers_credit_advanced" ? 500 :
      activeCustomersTier === "customers_credit_unlimited" ? Number.POSITIVE_INFINITY :
      0;
    return { productsLimit, usersLimit, customersLimit };
  }, [activeProductsTier, activeUsersTier, activeCustomersTier]);

  return {
    loading,
    reload: load,
    modules,
    isEnabled,
    getStatus,
    activeProductsTier,
    activeUsersTier,
    activeCustomersTier,
    limits,
  };
};
